import type { JobMarketDto } from 'src/contracts/job-market';

// Local storage keys
export const LOCAL_JOBS_KEY = 'jobjourney_local_jobs';
const LOCAL_JOBS_STRIPPED_KEY = 'jobjourney_local_jobs_stripped';
const LOCAL_JOB_SEARCH_SUMMARY_KEY = 'jobjourney_local_jobs_summary';

// ~4.5 MB threshold (localStorage limit is ~5 MB)
const SIZE_THRESHOLD = 4_500_000;

export type StoredJobSearchSummary = {
  location?: string | null;
  platforms?: string[];
  query?: string | null;
  totalJobsFound?: number | null;
};

/**
 * Gets jobs from local storage
 * @returns Array of JobMarketDto objects or empty array if none found
 */
export const getJobsFromStorage = (): JobMarketDto[] => {
  try {
    const jobsData = localStorage.getItem(LOCAL_JOBS_KEY);
    if (jobsData) {
      return JSON.parse(jobsData);
    }
    return [];
  } catch (error) {
    console.error('Error retrieving jobs from local storage:', error);
    return [];
  }
};

/**
 * Saves jobs to local storage with size awareness.
 * If the payload exceeds ~4.5 MB, strips description fields and retries.
 * If still too large, skips localStorage entirely.
 * @param jobs Array of JobMarketDto objects to save
 */
export const saveJobsToStorage = (jobs: JobMarketDto[]): void => {
  try {
    const serialized = JSON.stringify(jobs);

    if (serialized.length <= SIZE_THRESHOLD) {
      localStorage.setItem(LOCAL_JOBS_KEY, serialized);
      localStorage.removeItem(LOCAL_JOBS_STRIPPED_KEY);
      console.log(`Saved ${jobs.length} jobs to local storage (${(serialized.length / 1024).toFixed(1)} KB)`);
      return;
    }

    // Too large — strip description fields and retry
    console.warn(`Jobs payload too large (${(serialized.length / 1024).toFixed(1)} KB). Stripping descriptions...`);
    const strippedJobs = jobs.map(({ description, ...rest }) => rest) as JobMarketDto[];
    const strippedSerialized = JSON.stringify(strippedJobs);

    if (strippedSerialized.length <= SIZE_THRESHOLD) {
      localStorage.setItem(LOCAL_JOBS_KEY, strippedSerialized);
      localStorage.setItem(LOCAL_JOBS_STRIPPED_KEY, 'true');
      console.log(`Saved ${strippedJobs.length} jobs (descriptions stripped) to local storage (${(strippedSerialized.length / 1024).toFixed(1)} KB)`);
      return;
    }

    // Still too large — skip entirely
    console.warn(`Jobs payload still too large after stripping (${(strippedSerialized.length / 1024).toFixed(1)} KB). Skipping local storage.`);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('LocalStorage quota exceeded. Skipping save.');
    } else {
      console.error('Error saving jobs to local storage:', error);
    }
  }
};

export const getJobSearchSummaryFromStorage = (): StoredJobSearchSummary | null => {
  try {
    const summaryData = localStorage.getItem(LOCAL_JOB_SEARCH_SUMMARY_KEY);

    return summaryData ? JSON.parse(summaryData) : null;
  } catch (error) {
    console.error('Error retrieving job search summary from local storage:', error);
    return null;
  }
};

export const saveJobSearchSummaryToStorage = (summary: StoredJobSearchSummary | null): void => {
  try {
    if (!summary) {
      localStorage.removeItem(LOCAL_JOB_SEARCH_SUMMARY_KEY);
      return;
    }

    localStorage.setItem(LOCAL_JOB_SEARCH_SUMMARY_KEY, JSON.stringify(summary));
  } catch (error) {
    console.error('Error saving job search summary to local storage:', error);
  }
};

export const clearJobSearchSummaryStorage = (): void => {
  try {
    localStorage.removeItem(LOCAL_JOB_SEARCH_SUMMARY_KEY);
  } catch (error) {
    console.error('Error clearing job search summary from local storage:', error);
  }
};

/**
 * Clears jobs from local storage
 */
export const clearStorageJobs = (): void => {
  try {
    localStorage.removeItem(LOCAL_JOBS_KEY);
    localStorage.removeItem(LOCAL_JOBS_STRIPPED_KEY);
    clearJobSearchSummaryStorage();
    console.log('Cleared jobs from local storage');
  } catch (error) {
    console.error('Error clearing jobs from local storage:', error);
  }
};

/**
 * Checks if there are jobs in local storage
 * @returns boolean indicating if there are jobs in local storage
 */
export const hasJobsInStorage = (): boolean => {
  try {
    const jobs = getJobsFromStorage();
    return jobs.length > 0;
  } catch (error) {
    console.error('Error checking for jobs in local storage:', error);
    return false;
  }
};
